"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";

export const AssetTicketContext = createContext();

export const AssetTicketProvider = ({ children }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // Fetch all tickets
  const fetchTickets = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/tickets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.status === 200) {
        setTickets(data.data);
      } else {
        setError(data.message || "Failed to fetch tickets");
        toast.error(data.message || "Failed to fetch tickets");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching tickets");
      
    } finally {
      setLoading(false);
    }
  };

  // Raise Ticket
  const raiseTicket = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/ticket`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === 200) {
        await fetchTickets();
        toast.success(data.message || "Ticket raised successfully");

        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to raise ticket");
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error raising ticket");

      return {
        success: false,
        message: "Error raising ticket",
      };
    } finally {
      setLoading(false);
    }
  };

  // Approve Ticket
  const approveTicket = async (id, status, approved_audit_id) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(
        `${BASE_URL}/assets/ticket/approve/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status,
            approved_audit_id,
          }),
        }
      );

      const data = await res.json();

      if (data.status === 200) {
        await fetchTickets();
        toast.success(data.message || "Ticket updated successfully");

        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to update ticket");
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error updating ticket");

      return {
        success: false,
        message: "Error updating ticket",
      };
    } finally {
      setLoading(false);
    }
  };

  // Delete Ticket
  const deleteTicket = async (id, delete_audit_id) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/tickets/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      const data = await res.json();

      if (data.status === 200) {
        await fetchTickets();
        toast.success(data.message || "Ticket deleted successfully");

        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to delete ticket");
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error deleting ticket");

      return {
        success: false,
        message: "Error deleting ticket",
      };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchTickets();
    }
  }, []);

  return (
    <AssetTicketContext.Provider
      value={{
        tickets,
        loading,
        error,
        fetchTickets,
        raiseTicket,
        approveTicket,
        deleteTicket,
      }}
    >
      {children}
    </AssetTicketContext.Provider>
  );
};

export const useAssetTickets = () => useContext(AssetTicketContext);